import React, { useState } from 'react';
import { Box, Typography, Breadcrumbs, Link, Alert, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useCreateCompany,
  useAddCompanyAddress,
  usePatchCompany
} from '../../hooks/useCompany';
import CompanyForm, { TranslatableFormData } from '../../components/company/CompanyForm';
import { AddressCreateDTO, CompanyCreateDTO } from '../../models/Company';
import { LanguageCode } from '../../models/enums/LanguageCode';

const CompanyCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const createCompanyMutation = useCreateCompany();
  const addAddressMutation = useAddCompanyAddress();
  const patchCompanyMutation = usePatchCompany();
  
  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
  
  // Empty initialization objects with proper structure
  const emptyInitialData = {
    legalName: '',
    tradingName: '',
    industry: '',
    companySize: '',
    companyType: '',
    website: '',
    description: '',
    market: '',
    geographicPresence: '',
  };

  const emptyAddress = {
    id: 0,
    fullAddress: '',
    street: '',
    streetNumber: '',
    postalCode: '',
    city: '',
    region: '',
    country: '',
    countryCode: '',
    latitude: undefined,
    longitude: undefined,
    placeId: '',
    createdAt: new Date().toISOString()
  };
  
  const handleCreateCompany = async (
    companyData: CompanyCreateDTO,
    addressData?: AddressCreateDTO,
    translatableData?: TranslatableFormData
  ) => {
    try {
      // Create company with FR values
      const createdCompany = await createCompanyMutation.mutateAsync(companyData);

      // If address data exists, add it to the company
      if (addressData && createdCompany?.id) {
        await addAddressMutation.mutateAsync({
          companyId: createdCompany.id,
          address: addressData,
          addressTypeCode: 'HEADQUARTERS',
          isDefault: true
        });
      }

      // Save non-primary language translations via PATCH after creation
      if (translatableData && createdCompany?.id) {
        const translatableFields = ["description", "market", "geographicPresence"];

        for (const { languageCode, fields } of translatableData.modifiedLanguages) {
          if (languageCode === LanguageCode.Fr) continue;

          const langPatchOps = translatableFields
            .filter((field) => fields[field] !== undefined && fields[field] !== "")
            .map((field) => ({
              op: "replace",
              path: `/${field}`,
              value: fields[field],
            }));

          if (langPatchOps.length > 0) {
            await patchCompanyMutation.mutateAsync({
              id: createdCompany.id,
              operations: langPatchOps,
              languageCode: languageCode.toLowerCase(),
            });
          }
        }
      }

      setAlertSeverity('success');
      setAlertMessage(t('company.createdSuccessfully'));
      setAlertOpen(true);

      setTimeout(() => {
        navigate('/companies');
      }, 1500);
    } catch (error) {
      console.error('Error creating company:', error);
      setAlertSeverity('error');
      setAlertMessage(t('company.creationError'));
      setAlertOpen(true);
    }
  };
  
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
  
  const handleNavigateToList = () => {
    navigate('/companies');
  };
  
  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h2">{t('company.createCompany')}</Typography>
      </Box>
      
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToList}
          sx={{ cursor: 'pointer' }}
        >
          {t('company.companyList')}
        </Link>
        <Typography color="text.primary">{t('company.createNew')}</Typography>
      </Breadcrumbs>
      
      <CompanyForm
        initialData={emptyInitialData}
        initialAddress={emptyAddress}
        onSubmit={handleCreateCompany}
        isSubmitting={createCompanyMutation.isPending || addAddressMutation.isPending}
        submitButtonText={t('company.create')}
      />
      
      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleAlertClose} 
          severity={alertSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyCreate;